import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Handle token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');
        const response = await axios.post(`${API_URL}/auth/refresh-token`, {
          refreshToken,
        });

        const accessToken = response.data?.data?.accessToken || response.data?.accessToken;
        if (!accessToken) {
          throw new Error('Refresh token response missing access token');
        }
        localStorage.setItem('token', accessToken);

        originalRequest.headers.Authorization = `Bearer ${accessToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem('token');
        localStorage.removeItem('refreshToken');
        window.location.href = '/login';
        return Promise.reject(refreshError);
      }
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const authAPI = {
  register: (userData) => api.post('/auth/register', userData),
  login: (credentials) => api.post('/auth/login', credentials),
  googleLogin: (credential) => api.post('/auth/google', { credential }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  resetPassword: (token, password) => api.post(`/auth/reset-password/${token}`, { password }),
};

// User API calls
export const userAPI = {
  updateProfile: (profileData) => api.put('/users/profile', profileData),
  changePassword: (passwordData) => api.put('/users/change-password', passwordData),
  getServices: () => api.get('/users/services'),
};

// Booking API calls
export const bookingAPI = {
  create: (bookingData) => api.post('/bookings', bookingData),
  getMyBookings: () => api.get('/bookings'),
  getById: (id) => api.get(`/bookings/${id}`),
  update: (id, data) => api.put(`/bookings/${id}`, data),
  cancel: (id) => api.put(`/bookings/${id}/cancel`),
  cancelBooking: (id) => api.put(`/bookings/${id}/cancel`),
  getAllBarbers: () => api.get('/bookings/barbers'),
  getAvailableBarbers: (date, time) => api.get('/bookings/available-barbers', { params: { date, time } }),
  getBarberServices: (barberId) => api.get(`/bookings/barber-services/${barberId}`),
  previewAssignedBarber: (date, time) => api.get('/bookings/preview-barber', { params: { date, time } }),
  getPolicyContext: (date, time) => api.get('/bookings/policy-context', { params: { date, time } }),
  getLastCutPreset: () => api.get('/bookings/last-cut'),
  joinWaitlist: (payload) => api.post('/bookings/waitlist', payload),
  getMyWaitlist: () => api.get('/bookings/waitlist'),
  cancelWaitlistEntry: (id) => api.delete(`/bookings/waitlist/${id}`),
};

// Rating API calls
export const ratingAPI = {
  submitRating: (bookingId, ratingData) => api.post(`/ratings/${bookingId}`, ratingData),
  getBookingRating: (bookingId) => api.get(`/ratings/${bookingId}`),
};

// Payment API calls
export const paymentAPI = {
  chargeBooking: (bookingId, amount) => api.post('/payments/charge', { bookingId, amount }),
  refundPayment: (chargeId, amount, bookingId) => api.post('/payments/refund', { chargeId, amount, bookingId }),
  createSetupIntent: () => api.post('/payments/create-setup-intent'),
  verifyCard: (paymentMethodId) => api.post('/payments/verify-card', { paymentMethodId }),
  createCheckoutSessionForBooking: (bookingId) => api.post(`/payments/create-checkout-session/${bookingId}`),
};

// Assistant API calls (Admin only)
export const assistantAPI = {
  getMetrics: async () => {
    const response = await api.get('/admin/assistant/metrics');
    return response.data;
  },
  query: async (question, revealPII = false) => {
    const response = await api.post('/admin/assistant/query', {
      question,
      revealPII
    });
    return response.data;
  },
  getSchema: async () => {
    const response = await api.get('/admin/assistant/schema');
    return response.data;
  },
};

export default api;
